<script setup lang="ts">
import { useTheme } from 'vuetify'
import type { ApexOptions } from 'apexcharts'
import { useAuthStore } from '@stores/admin'
import { kNumberFormats } from '@core/utils/formatters'

definePage({
  meta: {
    auth: true,
    guard: 'admin',
    layout: 'admin',
  },
})

const { admin } = useAuthStore()
const vuetifyTheme = useTheme()
const currentTheme = vuetifyTheme.current.value.colors

const chartOptions: ApexOptions = {
  plotOptions: {
    bar: {
      columnWidth: '20%',
    },
  },
  chart: {
    toolbar: {
      show: false,
    },
    foreColor: 'white',
    type: 'bar',
    height: 250,
    zoom: {
      autoScaleYaxis: true,
    },
  },
  markers: {
    size: 0,
    style: 'hollow',
  },
  colors: [currentTheme.success, currentTheme.info, currentTheme.warning, currentTheme.primary],
  tooltip: {
    x: {
      format: 'dd MMM yyyy',
    },
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.7,
      opacityTo: 0.9,
      stops: [0, 100],
    },
  },
  annotations: {
    yaxis: [{
      borderColor: '#999',
    }],
    xaxis: [{
      borderColor: '#999',
    }],
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    width: 2,
    curve: 'smooth',
  },
  xaxis: {
    type: 'date',
    tickAmount: 6,
    stepSize: 1,

  },
  yaxis: {
    stroke: {
      width: 0,
    },
    labels: {
      formatter(val: number, opts?: any): string | string[] {
        return kNumberFormats(val)
      },
    },
    show: true,
  },
}

const statisticsVertical = reactive({
  title: 'Monthly Revenue',
  color: 'success',
  stats: '',
  height: 250,
  series: [
    {
      name: 'Users',
      data: [],
    },
  ],
  chartOptions,
})

const data = ref({
  total_revenue: 0,
  users_count: 0,
  users_without_apis: 0,
  users_without_subscription: 0,
  month_revenue: 0,
  active_bots: 0,
  pending: {
    affiliate_withdrawals: 0,
    new_bot_invoices: 0,
    extension_invoices: 0,
    add_fund_invoices: 0,
  },
  monthly_revenue: [],
})

const getStats = async () => {
  data.value = await $api('/api/admin/home')
  statisticsVertical.series = data.value.monthly_revenue

  // statisticsVertical.series[0].data = data.value.monthly_revenue
}

onMounted(() => {
  getStats()
})
</script>

<template>
  <VRow>
    <!-- 👉 Website analytics -->

    <!-- 👉 Sales Overview -->
    <!--
      <VCol
      cols="12"
      md="3"
      sm="6"
      >
      <AnalyticsSalesOverview />
      </VCol>
    -->

    <!-- 👉 Statistics Vertical -->

    <VCol cols="8">
      <VCard>
        <VCardTitle>
          <h5 class="text-h5 mb-2">
            Welcome back, <span class="text-h4"> {{ admin!.name }} 👋🏻 </span>
          </h5>
        </VCardTitle>
        <VCardSubtitle>
          Admin Dashboard
        </VCardSubtitle>
        <VCardText class="d-flex justify-space-between">
          <div>
            <div class="d-flex align-center">
              <VAvatar
                color="primary"
                size="54"
                variant="tonal"
                rounded="sm"
                class="me-4"
              >
                <VIcon
                  icon="tabler-user"
                  size="38"
                />
              </VAvatar>
              <div>
                <h6 class="text-h6 text-medium-emphasis">
                  All Users
                </h6>
                <h4 class="text-h4 text-primary">
                  {{ kNumberFormats(data.users_count) }}
                </h4>
              </div>
            </div>
          </div>
          <div>
            <div class="d-flex align-center">
              <VAvatar
                color="warning"
                size="54"
                variant="tonal"
                rounded="sm"
                class="me-4"
              >
                <VIcon
                  icon="tabler-user-off"
                  size="38"
                />
              </VAvatar>
              <div>
                <h6 class="text-h6 text-medium-emphasis">
                  Users without API
                </h6>
                <h4 class="text-h4 text-warning">
                  {{ kNumberFormats(data.users_without_apis) }}
                </h4>
              </div>
            </div>
          </div>
          <div>
            <div class="d-flex align-center">
              <VAvatar
                color="error"
                size="54"
                variant="tonal"
                rounded="sm"
                class="me-4"
              >
                <VIcon
                  icon="tabler-user-pause"
                  size="38"
                />
              </VAvatar>
              <div>
                <h6 class="text-h6 text-medium-emphasis">
                  Users without Subscriptions
                </h6>
                <h4 class="text-h4 text-error">
                  {{ kNumberFormats(data.users_without_subscription) }}
                </h4>
              </div>
            </div>
          </div>
          <div>
            <div class="d-flex align-center">
              <VAvatar
                color="info"
                size="54"
                variant="tonal"
                rounded="sm"
                class="me-4"
              >
                <VIcon
                  icon="tabler-currency-dollar"
                  size="38"
                />
              </VAvatar>
              <div>
                <h6 class="text-h6 text-medium-emphasis">
                  Total Revenue
                </h6>
                <h4 class="text-h4 text-info">
                  {{ kNumberFormats(data.total_revenue) }}
                </h4>
              </div>
            </div>
          </div>
        </VCardText>
      </VCard>
      <VRow class="mt-4">
        <VCol cols="4">
          <CardStatisticsHorizontal
            title="Active Subscriptions"
            color="primary"
            icon="tabler-copy-check"
            stats="25"
          />
        </VCol>
        <VCol cols="4">
          <CardStatisticsHorizontal
            title="This Month Revenue"
            color="primary"
            icon="tabler-archive"
            :stats="kNumberFormats(data.month_revenue)"
          />
        </VCol>
        <VCol cols="4">
          <CardStatisticsHorizontal
            title="Active Bots"
            color="primary"
            icon="tabler-tag"
            :stats="kNumberFormats(data.active_bots)"
          />
        </VCol>
      </VRow>
    </VCol>
    <VCol cols="4">
      <VCard title="Pending">
        <VList>
          <VDivider />
          <VListItem title="New Bot Invoices">
            <template #append>
              <span class="pe-2">{{ data.pending.new_bot_invoices }}</span>
            </template>
          </VListItem>
          <VDivider />
          <VListItem title="Extension Invoices">
            <template #append>
              <span class="pe-2">{{ data.pending.extension_invoices }}</span>
            </template>
          </VListItem>
          <VDivider />
          <VListItem title="Add Fund Invoices">
            <template #append>
              <span class="pe-2">{{ data.pending.add_fund_invoices }}</span>
            </template>
          </VListItem>
          <VDivider />
          <VListItem title="Affiliate Withdraw Requests">
            <template #append>
              <span class="pe-2">{{ data.pending.affiliate_withdrawals }}</span>
            </template>
          </VListItem>
        </VList>
      </VCard>
    </VCol>
    <VCol cols="12">
      <VRow>
        <VCol>
          <CardStatisticsVertical v-bind="statisticsVertical" />
        </VCol>
      </VRow>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core-scss/template/libs/apex-chart.scss";
</style>
